<template>
  <v-app>
    <div id="map"></div>
    <button id="home" class="mapboxgl-ctrl-icon home" type="button" aria-label="Home"><v-icon>home</v-icon></button>
    <v-container fluid>
      <v-layout row wrap class="controls">
        <v-flex v-if="showCities" class="cities">
          <v-select
            :items="cities"
            v-model="selectedCity"
            label="Search for a city"
            autocomplete
            :disabled="loading"
            :loading="loading"
            solo
            prepend-icon="search"
            clearable
            ></v-select>
        </v-flex>
        <v-flex v-if="showSubjects" class="subjects">
          <v-select
            :items="subjects"
            v-model="selectedSubject"
            label="Subject"
            :loading="loading"
            min-width="150"
            solo
            ></v-select>
        </v-flex>
        <v-flex v-if="showSectors" class="sectors">
          <v-select
            :items="sectors"
            v-model="selectedSector"
            label="Sector"
            :loading="loading"
            min-width="150"
            solo
            ></v-select>
        </v-flex>
        <v-flex v-if="showYears" class="years">
          <v-select
            :items="years"
            v-model="selectedYear"
            label="Year"
            :loading="!!loading"
            min-width="150"
            solo
            ></v-select>
        </v-flex>
      </v-layout>
      <v-layout row wrap class="infobox" elevation-5 v-if="selectedCity">
        <v-flex>
          <v-btn flat icon class="close" @click.native="closeInfobox">
            <v-icon>close</v-icon>
          </v-btn>
          <h1 class="headline">{{citiesIndex[selectedCity] && citiesIndex[selectedCity].city}}</h1>
          <h2 class="subtitle">{{citiesIndex[selectedCity] && citiesIndex[selectedCity].country}}</h2>
          <v-btn
            flat
            v-if="citiesIndex[selectedCity] && citiesIndex[selectedCity].href_map"
            :href="citiesIndex[selectedCity].href_map"
            target="_blank"
            rel="noopener"
            color="primary"
            >
            <v-icon>open_in_new</v-icon>
            View City Collaboration Map
          </v-btn>
          <v-layout class="counts">
            <template v-if="articleCount === null">
              <v-flex class="center single" v-if="fractionalCount !== null">
                <div>FC</div>
                <div>Fractional Count</div>
              </v-flex>
              <v-flex class="center" v-if="fractionalCount !== null">
                <div class="display-3">{{fractionalCount}}</div>
              </v-flex>
            </template>
            <template v-else-if="fractionalCount === null">
              <v-flex class="center single" v-if="articleCount !== null">
                <div>AC</div>
                <div>Article Count</div>
              </v-flex>
              <v-flex class="center" v-if="articleCount !== null">
                <div class="display-3">{{articleCount}}</div>
              </v-flex>
            </template>
            <template v-else>
              <v-flex class="center" v-if="articleCount !== null">
                <div>AC</div>
                <div class="display-3">{{articelCount}}</div>
                <div>Article Count</div>
              </v-flex>
              <v-flex class="center" v-if="fractionalCount !== null">
                <div>FC</div>
                <div class="display-3">{{fractionalCount}}</div>
                <div>Fractional Count</div>
              </v-flex>
            </template>
          </v-layout>
          <v-layout class="description" v-if="fractionalCount !== null">
            <div><b>Fractional Count</b> measures share of authorship, which accounts for the proportion of authors from the city on each article.</div>
          </v-layout>
          <v-data-table
            class="institutionsList"
            :headers="institutionsHeader"
            :items="institutions"
            :rows-per-page-items="[-1]"
            hide-actions
            must-sort
            :pagination.sync="institutionsPagination"
            >
            <template slot="items" slot-scope="props">
              <td>
                <template v-if="props.item.href">
                  <a :href="props.item.href" target="_blank" rel="noopener">
                    {{props.item.name}}
                  </a>
                </template>
                <template v-else="props.item.href">
                  {{props.item.name}}
                </template>
              </td>
              <td class="text-xs-center">{{props.item.fc}}</td>
            </template>
          </v-data-table>
          <div class="text-align: center">
            <img :src="citiesIndex[selectedCity].href_logo" v-if="citiesIndex[selectedCity] && citiesIndex[selectedCity].href_logo"/>
          </div>
        </v-flex>
      </v-layout>
    </v-container>
    <v-menu
      v-model="multipleCitiesMenu"
      :position-x="multipleCitiesMenuX"
      :position-y="multipleCitiesMenuY"
      absolute
      offset-y
      >
      <v-list>
        <v-list-tile v-for="city in multipleCities" :key="city.city" @click="multipleCitiesMenuSelect">
          <v-list-tile-content>
            <v-list-tile-title :data-value="city.city + ', ' + city.country">{{ city.city }}</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </v-list>
    </v-menu>
  </v-app>
</template>

<script>
import querystring from 'querystring'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import {json} from 'd3-fetch'
import {uniq, set, get, cloneDeep} from 'lodash'

mapboxgl.accessToken = 'pk.eyJ1IjoibmF0dXJlLWNvbGxhYm9yYXRpb25zIiwiYSI6ImNpdWV6c28yejAwZjUyenFmZDF6bmlicHUifQ.Z0HpTfWsqCtTIIQ27px8hQ'

var q = querystring.parse(location.search.substring(1))
var controls = {}
if (q.controls) {
  q.controls.split(',').forEach(function (control) {
    controls[control] = true
  })
}

export default {
  mounted () {
    var self = this
    json(q.data).then((cities) => {
      self.citiesIndex = {}

      var years = []
      var subjects = []
      var sectors = []

      // populate citiesIndex
      self.cities = cities
        .filter((city) => {
          // filter out cities without any coordinates
          return (city.lon && city.lat)
        })
        .map((city) => {
          const FC = cloneDeep(city.FC)
          self.citiesIndex[city.city + ', ' + city.country] = city
          if (city.institutions) {
            self.citiesIndex[city.city + ', ' + city.country].institutions = city.institutions.map((institution) => {
              var indexedFC = {}
              institution.FC.map((fc) => {
                indexedFC[[fc.year, fc.subject, fc.sector].join('_')] = fc.FC
              })
              institution.FC = indexedFC
              return institution
            })
          }
          self.citiesIndex[city.city + ', ' + city.country].FChash = {}
          FC.forEach((fc) => {
            set(self.citiesIndex[city.city + ', ' + city.country].FChash, [fc.year, fc.subject, fc.sector], fc.FC)
            years.push(fc.year)
            subjects.push(fc.subject)
            sectors.push(fc.sector)

            // identify maximum for each filter to scale circle sizes
            if (!(`FC_${fc.year}_${fc.subject}_${fc.sector}` in self.maxFCs)) {
              self.maxFCs[`FC_${fc.year}_${fc.subject}_${fc.sector}`] = 0
            }
            self.maxFCs[`FC_${fc.year}_${fc.subject}_${fc.sector}`] = Math.max(self.maxFCs[`FC_${fc.year}_${fc.subject}_${fc.sector}`], fc.FC)
          })
          return city.city + ', ' + city.country
        }).sort() // sort alphabetically

      years = uniq(years)
      subjects = uniq(subjects)
      sectors = uniq(sectors)

      self.loading = false

      self.citiesGeoJSON = {
        type: 'FeatureCollection',
        features: cities
          .filter((city) => {
            return (city.lon && city.lat)
          })
          .map((city) => {
            var feature = {
              type: 'Feature',
              geometry: { 'type': 'Point', coordinates: [Number(city.lon), Number(city.lat)] },
              properties: {
                city: city.city,
                country: city.country
              }
            }
            city.FC.map((FC) => {
              feature.properties[`FC_${FC.year}_${FC.subject}_${FC.sector}`] = FC.FC
            })
            return feature
          })
      }
      self.years = uniq(years).map((year) => { return year }).sort((a, b) => {
        return a < b
      })
      self.subjects = uniq(subjects).map((subject) => { return subject }).sort()
      self.sectors = uniq(sectors).map((sector) => { return sector }).sort()

      if (!self.selectedYear) {
        self.selectedYear = self.years.length ? self.years[0] : '' // default latest year
      }

      if (!self.selectedSubject) {
        self.selectedSubject = self.subjects.length ? self.subjects[0] : ''
      }

      if (!self.selectedSector) {
        self.selectedSector = self.sectors.length ? self.sectors[0] : ''
      }

      self.tryAddMapLayers()
    })
    this.map = new mapboxgl.Map({
      container: 'map',
      style: 'mapbox://styles/nature-collaborations/ciuezwr48006l2iru88m4k2ml',
      center: [0, 0],
      zoom: 0.01,
      maxzoom: 8,
      hash: true,
      dragRotate: false
    })
    this.map.on('load', function () {
      self.mapLoaded = true
      self.tryAddMapLayers()
    })
    this.map.addControl(new mapboxgl.NavigationControl({ showCompass: false }))

    if (controls.fullscreen) {
      this.map.addControl(new mapboxgl.FullscreenControl())
    }

    if (q.initialBounds) {
      this.map.fitBounds(q.initialBounds.split(','), { duration: 0 })
    }

    if (q.lat && q.lng && q.zoom) {
      document.getElementById('home').addEventListener('click', function () {
        this.map.flyTo({
          center: [q.lng, q.lat],
          zoom: q.zoom
        })
      }.bind(this))
    }
  },
  data () {
    return {
      loading: true,
      citiesGeoJSON: null,
      mapLoaded: false,

      primaryColor: q.color || 'ff9e9e',
      sizeMultiplier: (q.sizeMultiplier ? Number(q.sizeMultiplier) : 1) / 10,

      // domain of controls
      cities: [],
      years: [],
      subjects: [],
      sectors: [],

      // initially selected values
      selectedCity: q.city,
      selectedYear: q.year,
      selectedSubject: q.subject,
      selectedSector: q.sector,

      citiesIndex: {},

      // toggles to show or hide controls
      showCities: controls.cities,
      showYears: controls.years,
      showSubjects: controls.subjects,
      showSectors: controls.sectors,

      multipleCities: null,
      multipleCitiesMenu: false,
      multipleCitiesMenuX: 0,
      multipleCitiesMenuY: 0,

      hoveredCityId: null,

      maxFCs: {},

      institutionsHeader: [
        {
          text: 'Top Institutions',
          align: 'left',
          sortable: false,
          value: 'name'
        },
        {
          text: 'FC',
          align: 'center',
          sortable: false,
          value: 'fc'
        }
      ],

      institutionsPagination: {
        sortBy: 'fc',
        descending: true
      }
    }
  },
  methods: {
    tryAddMapLayers: function () {
      if (!this.citiesGeoJSON || !this.mapLoaded) {
        return // wait for both map load and data downloaded
      }
      var self = this
      self.map.addSource('cities', {
        type: 'geojson',
        data: this.citiesGeoJSON
      })
      self.map.addSource('cities-hover', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      })
      self.map.addLayer({
        id: 'cities-hover',
        source: 'cities-hover',
        type: 'circle',
        paint: {
          'circle-opacity': 0,
          'circle-radius': 0,
          'circle-blur': 0.2,
          'circle-stroke-width': 7,
          'circle-stroke-opacity': 1,
          'circle-stroke-color': '#' + self.primaryColor
        }
      })
      self.map.addLayer({
        id: 'cities',
        source: 'cities',
        type: 'circle',
        paint: {
          'circle-opacity': 0.5,
          'circle-radius': 0,
          'circle-color': '#' + self.primaryColor,
          'circle-stroke-width': 0.5,
          'circle-stroke-opacity': 0.5,
          'circle-stroke-color': 'black'
        }
      })
      /*
            [
              'case',
              ['boolean', ['feature-state', 'hover'], false],
              2,
              0.5
            ],
       */
      self.map.addLayer({
        id: 'cities-selected',
        source: 'cities',
        type: 'circle',
        paint: {
          'circle-opacity': 0,
          'circle-radius': 0,
          'circle-stroke-width': 4,
          'circle-stroke-opacity': 1,
          'circle-stroke-color': '#' + self.primaryColor
        },
        filter: ['==', 'city', '']
      })
      self.map.addLayer({
        id: 'cities-label',
        source: 'cities',
        type: 'symbol',
        paint: {
          'text-color': '#383838',
          'text-halo-color': 'hsla(0, 0%, 100%, 0.5)',
          'text-halo-width': 2
        },
        layout: {
          'text-field': ['to-string', ['get', 'city']],
          'text-font': ['Open Sans Regular'],
          'text-size': 0
        }
      })
      self.filterUpdated()
      self.map.on('mouseenter', 'cities', function () { self.map.getCanvas().style.cursor = 'pointer' })
      self.map.on('mouseleave', 'cities', function () {
        self.map.getCanvas().style.cursor = ''

        self.map.getSource('cities-hover').setData({
          type: 'FeatureCollection',
          features: []
        })
      })
      self.map.on('mousemove', 'cities', function (e) {
        self.map.getSource('cities-hover').setData({
          type: 'FeatureCollection',
          features: e.features
        })

        /*
          if (e.features.length) {
            if (self.hoveredCityId) {
              self.map.setFeatureState({
                source: 'cities',
                id: self.hoveredCityId
              }, {
                hover: false
              })
            }
            self.hoveredCityId = e.features[0].id
            self.map.setFeatureState({
              source: 'cities',
              id: self.hoveredCityId
            }, {
              hover: true
            })
          }
         */
      })
      self.map.on('click', function (e) {
        const features = self.map.queryRenderedFeatures(e.point, {
          layers: ['cities']
        })

        if (features && features.length) {
          if (features.length === 1) {
            var feature = features[0]
            self.selectedCity = feature.properties.city + ', ' + feature.properties.country
            self.multipleCities = []
            self.multipleCitiesMenu = false
          } else {
            self.multipleCities = features.map((city) => {
              return city.properties
            })
            self.multipleCitiesMenuX = e.point.x
            self.multipleCitiesMenuY = e.point.y
            self.$nextTick(() => {
              self.multipleCitiesMenu = true
            })
          }
        } else {
          self.multipleCities = []
          self.multipleCitiesMenu = false
        }
      })
    },
    filterUpdated: function () {
      const minSizeLow = 2 // FC of 0 will still have a radius of this
      const minSizeHigh = 15 // FC of 0 will still have a radius of this
      const maxFC = this.maxFCs[`FC_${this.selectedYear}_${this.selectedSubject}_${this.selectedSector}`]
      const scaleFactor = 2000 / ((maxFC > 2000) ? 2000 : (maxFC + 1))
      const circleRadius = [
        'interpolate',
        ['exponential', 1.9],
        ['zoom'],
        1,
        [
          '+',
          [
            '*',
            [
              'get',
              `FC_${this.selectedYear}_${this.selectedSubject}_${this.selectedSector}`
            ],
            0.02
          ],
          minSizeLow
        ],
        8,
        [
          '+',
          [
            '*',
            [
              'get',
              `FC_${this.selectedYear}_${this.selectedSubject}_${this.selectedSector}`
            ],
            this.sizeMultiplier * scaleFactor
          ],
          minSizeHigh
        ]
      ]
      const circleStrokeWidth = [
        'interpolate',
        ['exponential', 1.9],
        ['zoom'],
        1,
        [
          '*',
          [
            'get',
            `FC_${this.selectedYear}_${this.selectedSubject}_${this.selectedSector}`
          ],
          0.02 * 0.5 / 5
        ],
        8,
        [
          '*',
          [
            'get',
            `FC_${this.selectedYear}_${this.selectedSubject}_${this.selectedSector}`
          ],
          this.sizeMultiplier * scaleFactor * 3 / 20
        ]
      ]
      this.map.setPaintProperty('cities', 'circle-radius', circleRadius)
      this.map.setPaintProperty('cities-hover', 'circle-radius', circleRadius)
      this.map.setPaintProperty('cities-hover', 'circle-stroke-width', circleStrokeWidth)
      this.map.setPaintProperty('cities-selected', 'circle-radius', circleRadius)

      this.map.setLayoutProperty('cities-label', 'text-size',
        [
          'interpolate',
          ['exponential', 1.9],
          ['zoom'],
          1,
          [
            'max',
            [
              'min',
              [
                '*',
                [
                  'get',
                  `FC_${this.selectedYear}_${this.selectedSubject}_${this.selectedSector}`
                ],
                0.02
              ],
              150 // no bigger than this
            ],
            10 // no smaller than this
          ],
          8,
          [
            'max',
            [
              'min',
              [
                '*',
                [
                  'get',
                  `FC_${this.selectedYear}_${this.selectedSubject}_${this.selectedSector}`
                ],
                this.sizeMultiplier * scaleFactor
              ],
              150 // no bigger than this
            ],
            14 // no smaller than this
          ]
        ]
      )
    },
    closeInfobox: function () {
      this.selectedCity = null
    },
    filterUpdatedPotentiallyBeforeMapLoaded: function () {
      if (this.map.getLayer('cities')) {
        if (this.map.loaded()) {
          this.filterUpdated()
        } else {
          this.map.on('load', () => {
            this.filterUpdated()
          })
        }
      } // else filterUpdated get's called after the cities layer is added
    },
    multipleCitiesMenuSelect: function (e) {
      this.selectedCity = e.target.getAttribute('data-value')
    }
  },
  computed: {
    institutions: function () {
      if (!this.citiesIndex[this.selectedCity]) {
        return []
      }
      return this.citiesIndex[this.selectedCity].institutions.map((institution) => {
        return {
          name: institution.name,
          href: institution.href,
          fc: institution.FC[[this.selectedYear, this.selectedSubject, this.selectedSector].join('_')]
        }
      })
    },
    articleCount: function () {
      if (!this.citiesIndex[this.selectedCity]) {
        return null
      }
      return get(this.citiesIndex[this.selectedCity].AChash, [this.selectedYear, this.selectedSubject, this.selectedSector], null)
    },
    fractionalCount: function () {
      if (!this.citiesIndex[this.selectedCity]) {
        return null
      }
      return get(this.citiesIndex[this.selectedCity].FChash, [this.selectedYear, this.selectedSubject, this.selectedSector], null)
    }
  },
  watch: {
    selectedYear: function (val) {
      this.filterUpdatedPotentiallyBeforeMapLoaded()
    },
    selectedSubject: function (val) {
      this.filterUpdatedPotentiallyBeforeMapLoaded()
    },
    selectedSector: function (val) {
      this.filterUpdatedPotentiallyBeforeMapLoaded()
    },
    selectedCity: function (val) {
      if (val && this.citiesIndex[val].lat && this.citiesIndex[val].lon) {
        this.map.easeTo({
          center: [this.citiesIndex[val].lon, this.citiesIndex[val].lat],
          zoom: (this.map.getZoom() > 7) ? this.map.getZoom() : 7,
          offset: [314 / 2, 0], // width of the infobox / 2
          duration: 750
        })
      }

      if (this.map.getLayer('cities-selected')) {
        // eslint-disable-next-line
        this.map.setFilter('cities-selected', ['==', 'city', val ? val.split(', ')[0] : ''])
      }
    }
  },
  name: 'App'
}
</script>
<style>
  html { overflow-y: auto !important; }

  .center {
    text-align: center;
  }

  #map {
    position: absolute;
    padding: 0;
    margin: 0;
    height: 100%;
    width: 100%;
  }
  .home {
    position: absolute;
    padding: 3px;
    background: white;
    right: 8px;
    top: 75px;
    border: 2px solid #ddd;
    border-radius: 6px;
  }
  .controls {
    margin-right: 40px;
  }
  .controls > div {
    margin: 1rem 0rem 0 1rem;
    max-width: 10rem; /* ie */
    max-width: intrinsic; /* old safari */
    max-width: fit-content; /* modern */
  }
  .controls .cities {
    max-width: 300px;
  }

  .infobox {
    width: 300px;
    margin: 1rem;
    background-color: white;
    position: absolute;
  }
  .infobox .close {
    right: 0;
    position: absolute;
  }
  .infobox .headline {
    padding-left: 20px;
    padding-right: 20px;
    padding-top: 20px;

    font-family: Knockout;
    font-size: 42px !important;
    font-weight: 600;
    text-transform: uppercase;
  }
  .infobox .subtitle {
    padding-left: 20px;
    font-family: Knockout;
  }
  .infobox .counts {
    font-family: Knockout;
    background-color: #eee;
    padding: 1rem;
    text-transform: uppercase;
    font-weight: 600;
    font-size: 16px;
    line-height: 0.8;
  }
  .infobox .counts .single {
    line-height: 1.4;
  }
  .infobox .counts .single > div:first-child {
    font-size: 30px;
  }

  .infobox .counts > div:nth-child(2) {
    border-left: 1px solid #ccc;
  }
  .infobox .counts .display-3{
    line-height: 1 !important;
    font-family: KnockoutCondensed;
    font-size: 76px !important;
  }

  .infobox .description {
    padding: 0.5rem;
    font-stretch: condensed;
  }

  .infobox .institutions {
    overflow-y: scroll;
  }

  table thead th {
    color: black !important;
    text-transform: uppercase;
    padding: 0px 8px !important;
    font-weight: 700 !important;
  }
  table thead tr{
    height: 30px !important;
  }
  .institutionsList {
    max-height: 200px;
    overflow-y: scroll;
  }

  table tbody td {
    height: 32px !important;
  }
  table tbody td {
    padding: 0px 8px !important;
  }

  .infobox img {
    max-width: 100%;
    padding: 10px;
  }

  .container {
    padding: 0px !important;
  }
</style>



// WEBPACK FOOTER //
// src/App.vue